// Vesting  Structure

import { ReactNode } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import {
  automaticVestingSharePlanTypeMap,
  exercisableVestingSharePlanTypeMap,
  SharePlanType,
} from '../../../types/pool-plans.types';

export enum VestingStructureColumn {
  FULL_VESTING = 'full-vesting',
  GRANTED = 'granted',
  NEXT_VESTING = 'next-vesting',
  REMAINING = 'remaining',
  TYPE = 'type',
  VESTED = 'vested',
}
export enum VestingPlanType {
  AUTOMATIC = 'AUTOMATIC',
  EXERCISABLE = 'EXERCISABLE',
}

export const tableVestingStructureColumnTitle: Record<VestingStructureColumn, ReactNode> = {
  [VestingStructureColumn.GRANTED]: <AppFormattedMessage id="GRANTED" />,
  [VestingStructureColumn.TYPE]: <AppFormattedMessage id="TYPE" />,
  [VestingStructureColumn.VESTED]: <AppFormattedMessage id="VESTED" />,
  [VestingStructureColumn.REMAINING]: <AppFormattedMessage id="REMAINING" />,
  [VestingStructureColumn.NEXT_VESTING]: <AppFormattedMessage id="NEXT_VESTING" />,
  [VestingStructureColumn.FULL_VESTING]: <AppFormattedMessage id="FULL_VESTING" />,
};

export const tableVestingStructureColumn = Object.keys(tableVestingStructureColumnTitle);

// Vesting Exercise Structure

export enum VestingExerciseStructureColumn {
  EXERCISABLE = 'exercisable',
  EXERCISE_BUTTON = 'exercise-button',
  EXERCISED = 'exercised',
  FULL_VESTING = 'full-vesting',
  GRANTED = 'granted',
  TO_BE_VESTED = 'to-be-vested',
  TYPE = 'type',
}

export const tableVestingExerciseStructureColumnTitle: Record<
  VestingExerciseStructureColumn,
  ReactNode
> = {
  [VestingExerciseStructureColumn.GRANTED]: <AppFormattedMessage id="GRANTED" />,
  [VestingExerciseStructureColumn.TYPE]: <AppFormattedMessage id="TYPE" />,
  [VestingExerciseStructureColumn.EXERCISABLE]: <AppFormattedMessage id="EXERCISABLE" />,
  [VestingExerciseStructureColumn.EXERCISED]: <AppFormattedMessage id="EXERCISED" />,
  [VestingExerciseStructureColumn.TO_BE_VESTED]: <AppFormattedMessage id="TO_BE_VESTED" />,
  [VestingExerciseStructureColumn.FULL_VESTING]: <AppFormattedMessage id="FULL_VESTING" />,
  [VestingExerciseStructureColumn.EXERCISE_BUTTON]: <AppFormattedMessage id="EMPTY" />,
};

export const tableVestingExerciseStructureColumn = Object.keys(
  tableVestingExerciseStructureColumnTitle,
);

// Common Stock, RSU, RSA (without RSA purchase price), Phantom

export enum PlanColumn {
  GRANT_ID = 'grant-id',
  PLAN = 'plan',
  PLAN_TYPE = 'plan-type',
  SHARE_CLASS = 'share-class',
}

export const tablePlanColumnTitle: Record<PlanColumn, ReactNode> = {
  [PlanColumn.PLAN]: <AppFormattedMessage id="PLAN" />,
  [PlanColumn.GRANT_ID]: <AppFormattedMessage id="GRANT_ID" />,
  [PlanColumn.PLAN_TYPE]: <AppFormattedMessage id="PLAN_TYPE" />,
  [PlanColumn.SHARE_CLASS]: <AppFormattedMessage id="SHARE_CLASS" />,
};

export const tablePlanColumn = Object.keys(tablePlanColumnTitle);

//Hurdle

export enum HurdlePlanColumn {
  CURRENT_VALUATION = 'current-valuation',
  GRANT_ID = 'grant-id',
  HURDLE = 'hurdle',
  PLAN = 'plan',
  PLAN_TYPE = 'plan-type',
  SHARE_CLASS = 'share-class',
}

export const tableHurdlePlanColumnTitle: Record<HurdlePlanColumn, ReactNode> = {
  [HurdlePlanColumn.PLAN]: <AppFormattedMessage id="PLAN" />,
  [HurdlePlanColumn.GRANT_ID]: <AppFormattedMessage id="GRANT_ID" />,
  [HurdlePlanColumn.PLAN_TYPE]: <AppFormattedMessage id="PLAN_TYPE" />,
  [HurdlePlanColumn.SHARE_CLASS]: <AppFormattedMessage id="SHARE_CLASS" />,
  [HurdlePlanColumn.CURRENT_VALUATION]: <AppFormattedMessage id="CURRENT_VALUATION" />,
  [HurdlePlanColumn.HURDLE]: <AppFormattedMessage id="HURDLE" />,
};
export const tableHurdlePlanColumn = Object.keys(tableHurdlePlanColumnTitle);

//SAR

export enum SARPlanColumn {
  BASE_PRICE = 'base-price',
  GRANT_ID = 'grant-id',
  PLAN = 'plan',
  PLAN_TYPE = 'plan-type',
  SHARE_CLASS = 'share-class',
}

export const tableSARPlanColumnTitle: Record<SARPlanColumn, ReactNode> = {
  [SARPlanColumn.PLAN]: <AppFormattedMessage id="PLAN" />,
  [SARPlanColumn.GRANT_ID]: <AppFormattedMessage id="GRANT_ID" />,
  [SARPlanColumn.PLAN_TYPE]: <AppFormattedMessage id="PLAN_TYPE" />,
  [SARPlanColumn.SHARE_CLASS]: <AppFormattedMessage id="SHARE_CLASS" />,
  [SARPlanColumn.BASE_PRICE]: <AppFormattedMessage id="BASE_PRICE" />,
};

export const tableSARPlanColumn = Object.keys(tableSARPlanColumnTitle);

//Stock Option

export enum StockOptionPlanColumn {
  CURRENT_PRICE = 'current-price',
  GRANT_ID = 'grant-id',
  PLAN = 'plan',
  PLAN_TYPE = 'plan-type',
  SHARE_CLASS = 'share-class',
  STRIKE_PRICE = 'strike-price',
}

export const tableStockOptionPlanColumnTitle: Record<StockOptionPlanColumn, ReactNode> = {
  [StockOptionPlanColumn.PLAN]: <AppFormattedMessage id="PLAN" />,
  [StockOptionPlanColumn.GRANT_ID]: <AppFormattedMessage id="GRANT_ID" />,
  [StockOptionPlanColumn.PLAN_TYPE]: <AppFormattedMessage id="PLAN_TYPE" />,
  [StockOptionPlanColumn.SHARE_CLASS]: <AppFormattedMessage id="SHARE_CLASS" />,
  [StockOptionPlanColumn.CURRENT_PRICE]: <AppFormattedMessage id="CURRENT_PRICE" />,
  [StockOptionPlanColumn.STRIKE_PRICE]: <AppFormattedMessage id="STRIKE_PRICE" />,
};

export const tableStockOptionPlanColumn = Object.keys(tableStockOptionPlanColumnTitle);

//Warrant

export enum WarrantPlanColumn {
  CURRENT_PRICE = 'current-price',
  GRANT_ID = 'grant-id',
  PLAN = 'plan',
  PLAN_TYPE = 'plan-type',
  SHARE_CLASS = 'share-class',
  WARRANT_PRICE = 'warrant-price',
}

export const tableWarrantPlanColumnTitle: Record<WarrantPlanColumn, ReactNode> = {
  [WarrantPlanColumn.PLAN]: <AppFormattedMessage id="PLAN" />,
  [WarrantPlanColumn.GRANT_ID]: <AppFormattedMessage id="GRANT_ID" />,
  [WarrantPlanColumn.PLAN_TYPE]: <AppFormattedMessage id="PLAN_TYPE" />,
  [WarrantPlanColumn.SHARE_CLASS]: <AppFormattedMessage id="SHARE_CLASS" />,
  [WarrantPlanColumn.CURRENT_PRICE]: <AppFormattedMessage id="CURRENT_PRICE" />,
  [WarrantPlanColumn.WARRANT_PRICE]: <AppFormattedMessage id="WARRANT_PRICE" />,
};

export const tableWarrantPlanColumn = Object.keys(tableWarrantPlanColumnTitle);

//RSA (With RSA purchase price)

export enum RSAWithPricePlanColumn {
  CURRENT_PRICE = 'current-price',
  GRANT_ID = 'grant-id',
  PLAN = 'plan',
  PLAN_TYPE = 'plan-type',
  RSA_PRICE = 'rsa-price',
  SHARE_CLASS = 'share-class',
}

// export const tableRSAWithPricePlanColumn: RSAWithPricePlanColumn[] =
//   Object.values(RSAWithPricePlanColumn);

// export const tableRSAWithPricePlanColumnTitle: Record<RSAWithPricePlanColumn, ReactNode> = {
//   [RSAWithPricePlanColumn.PLAN]: <AppFormattedMessage id="PLAN" />,
//   [RSAWithPricePlanColumn.GRANT_ID]: <AppFormattedMessage id="GRANT_ID" />,
//   [RSAWithPricePlanColumn.PLAN_TYPE]: <AppFormattedMessage id="PLAN_TYPE" />,
//   [RSAWithPricePlanColumn.SHARE_CLASS]: <AppFormattedMessage id="SHARE_CLASS" />,
//   [RSAWithPricePlanColumn.CURRENT_PRICE]: <AppFormattedMessage id="CURRENT_PRICE" />,
//   [RSAWithPricePlanColumn.RSA_PRICE]: <AppFormattedMessage id="RSA_PRICE" />,
// };

export type AllVestingColumnEnums = VestingExerciseStructureColumn | VestingStructureColumn;

export type AllVestingColumnRecords = Record<VestingExerciseStructureColumn, ReactNode> &
  Record<VestingStructureColumn, ReactNode>;

export type AllPlanColumnEnums =
  | HurdlePlanColumn
  | PlanColumn
  | RSAWithPricePlanColumn
  | SARPlanColumn
  | StockOptionPlanColumn
  | WarrantPlanColumn;

export type AllPlanColumnRecords = Record<HurdlePlanColumn, ReactNode> &
  Record<PlanColumn, ReactNode> &
  Record<RSAWithPricePlanColumn, ReactNode> &
  Record<SARPlanColumn, ReactNode> &
  Record<StockOptionPlanColumn, ReactNode> &
  Record<WarrantPlanColumn, ReactNode>;

export const vestingSharePlanType: Record<VestingPlanType, SharePlanType[]> = {
  [VestingPlanType.AUTOMATIC]: automaticVestingSharePlanTypeMap,
  [VestingPlanType.EXERCISABLE]: exercisableVestingSharePlanTypeMap,
};

export const table: Record<
  SharePlanType,
  {
    planColumns: AllPlanColumnEnums[];
    planColumnsTitle: Partial<AllPlanColumnRecords>;
    vestingColumns: AllVestingColumnEnums[];
    vestingColumnsTitle: Partial<AllVestingColumnRecords>;
  }
> = {
  [SharePlanType.COMMON_STOCK]: {
    vestingColumns: tableVestingStructureColumn,
    planColumns: tablePlanColumn,
    planColumnsTitle: tablePlanColumnTitle,
    vestingColumnsTitle: tableVestingStructureColumnTitle,
  },
  [SharePlanType.RSU]: {
    vestingColumns: tableVestingStructureColumn,
    planColumns: tablePlanColumn,
    planColumnsTitle: tablePlanColumnTitle,
    vestingColumnsTitle: tableVestingStructureColumnTitle,
  },
  [SharePlanType.RSA]: {
    vestingColumns: tableVestingStructureColumn,
    planColumns: tablePlanColumn,
    planColumnsTitle: tablePlanColumnTitle,
    vestingColumnsTitle: tableVestingStructureColumnTitle,
  },
  [SharePlanType.PHANTOM]: {
    vestingColumns: tableVestingStructureColumn,
    planColumns: tablePlanColumn,
    planColumnsTitle: tablePlanColumnTitle,
    vestingColumnsTitle: tableVestingStructureColumnTitle,
  },

  [SharePlanType.GROWTH_HURDLE]: {
    vestingColumns: tableVestingExerciseStructureColumn,
    planColumns: tableHurdlePlanColumn,
    planColumnsTitle: tableHurdlePlanColumnTitle,
    vestingColumnsTitle: tableVestingExerciseStructureColumnTitle,
  },
  [SharePlanType.SAR]: {
    vestingColumns: tableVestingExerciseStructureColumn,
    planColumns: tableSARPlanColumn,
    planColumnsTitle: tableSARPlanColumnTitle,
    vestingColumnsTitle: tableVestingExerciseStructureColumnTitle,
  },
  [SharePlanType.STOCK_OPTIONS]: {
    vestingColumns: tableVestingExerciseStructureColumn,
    planColumns: tableStockOptionPlanColumn,
    planColumnsTitle: tableStockOptionPlanColumnTitle,
    vestingColumnsTitle: tableVestingExerciseStructureColumnTitle,
  },
  [SharePlanType.WARRANTS]: {
    vestingColumns: tableVestingExerciseStructureColumn,
    planColumns: tableWarrantPlanColumn,
    planColumnsTitle: tableWarrantPlanColumnTitle,
    vestingColumnsTitle: tableVestingExerciseStructureColumnTitle,
  },
  [SharePlanType.BSPCE]: {
    vestingColumns: tableVestingExerciseStructureColumn,
    planColumns: tableStockOptionPlanColumn,
    planColumnsTitle: tableStockOptionPlanColumnTitle,
    vestingColumnsTitle: tableVestingExerciseStructureColumnTitle,
  },
};
